import React, { useEffect, useState } from 'react'
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  Box,
  CircularProgress,
} from '@material-ui/core'
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@material-ui/icons'
import {
  getEmailDomainRulesService,
  deleteEmailDomainRuleService,
} from 'src/api/email-domain-rule'
import RuleDialog from './components/rule-dialog'
import { Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText } from '@material-ui/core'

const EmailDomainRules = () => {
  const [rules, setRules] = useState([])
  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedRule, setSelectedRule] = useState(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [ruleToDelete, setRuleToDelete] = useState(null)

  const fetchRules = async () => {
    setLoading(true)
    try {
      const response = await getEmailDomainRulesService()
      if (response.data) {
        setRules(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch rules:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRules()
  }, [])

  const handleOpenDialog = (rule = null) => {
    setSelectedRule(rule)
    setDialogOpen(true)
  }

  const handleCloseDialog = (refresh = false) => {
    setDialogOpen(false)
    setSelectedRule(null)
    if (refresh) {
      fetchRules()
    }
  }

  const handleOpenDeleteDialog = rule => {
    setRuleToDelete(rule)
    setDeleteDialogOpen(true)
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setRuleToDelete(null)
  }

  const handleDeleteRule = async () => {
    if (!ruleToDelete) return

    try {
      await deleteEmailDomainRuleService(ruleToDelete.id)
      fetchRules()
    } catch (error) {
      console.error('Failed to delete rule:', error)
    } finally {
      handleCloseDeleteDialog()
    }
  }

  const getRuleTypeLabel = type => {
    switch (type) {
      case 1:
        return 'Whitelist'
      case 2:
        return 'Blacklist'
      default:
        return 'Unknown'
    }
  }

  return (
    <Container maxWidth="lg" style={{ marginTop: '24px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Email Domain Rules</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Rule
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Domain</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : rules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No rules found.
                </TableCell>
              </TableRow>
            ) : (
              rules.map(rule => (
                <TableRow key={rule.id}>
                  <TableCell>{rule.domain}</TableCell>
                  <TableCell>{getRuleTypeLabel(rule.ruleType)}</TableCell>
                  <TableCell>{new Date(rule.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenDialog(rule)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleOpenDeleteDialog(rule)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {dialogOpen && (
        <RuleDialog
          open={dialogOpen}
          initialData={selectedRule}
          onClose={handleCloseDialog}
        />
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Delete Rule</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the rule for {ruleToDelete?.domain}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteRule} color="secondary" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default EmailDomainRules
